import PropTypes from 'prop-types';
import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getUserNotifications, getUnreadNotificationCount, markNotificationAsRead } from '../api/api';

export const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  fetchNotifications: () => {},
  markAsRead: () => {},
});

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.id;

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [notifs, countRes] = await Promise.all([
        getUserNotifications(userId),
        getUnreadNotificationCount(userId)
      ]);
      setNotifications(notifs);
      setUnreadCount(countRes.count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const markAsRead = async (notificationId) => {
    if (!userId) return;
    try {
      await markNotificationAsRead(notificationId, userId);
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchNotifications();
      // Optional: Polling every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [userId, fetchNotifications]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead
  }), [notifications, unreadCount, loading, fetchNotifications]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node
};
